import { StrictMode, type ErrorInfo } from "react";
import { createRoot } from "react-dom/client";
import { initPWADiagnostics } from "./lib/pwaDiagnostics";
import { isSupabaseConfigured, envValidation } from "./integrations/supabase/client";
import App from "./App.tsx";
import { MaintenanceScreen } from "./components/MaintenanceScreen";
import { RootErrorScreen } from "./components/RootErrorScreen";
import { ErrorBoundary } from "./components/ErrorBoundary";
import "./index.css";
import "./i18n/i18n";
// Lazy-load PostHog to reduce initial bundle size / improve TTI
if (typeof window !== 'undefined' && import.meta.env.VITE_POSTHOG_KEY) {
  import('posthog-js').then(({ default: posthog }) => {
    posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
      api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com',
      person_profiles: 'identified_only',
      autocapture: false,
      disable_session_recording: true,
      capture_pageview: false,
    });
  });
}

// Initialize PWA diagnostics early
initPWADiagnostics();

// Recover from transient "Failed to fetch dynamically imported module" errors.
// These typically occur when Vite HMR briefly disconnects, or when a previously
// loaded chunk hash changes after a new deploy. A one-shot reload restores the app.
if (typeof window !== 'undefined') {
  const RELOAD_KEY = 'lov-chunk-reload-at';
  const isChunkLoadError = (msg: unknown) =>
    typeof msg === 'string' &&
    (msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Importing a module script failed') ||
      msg.includes("error loading dynamically imported module"));

  const tryReload = () => {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    // Avoid reload loops: only reload once per 10s window.
    if (Date.now() - last < 10_000) return;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    window.location.reload();
  };

  window.addEventListener('error', (e) => {
    if (isChunkLoadError(e.message)) tryReload();
  });
  window.addEventListener('unhandledrejection', (e) => {
    const reason: any = e.reason;
    const msg = reason?.message ?? String(reason ?? '');
    if (isChunkLoadError(msg)) tryReload();
  });
}

// Check if we just updated and show notification after React mounts
const justUpdated = sessionStorage.getItem('app-just-updated');
if (justUpdated) {
  sessionStorage.removeItem('app-just-updated');
  // Dispatch event after a short delay to ensure toast system is ready
  setTimeout(() => {
    window.dispatchEvent(new CustomEvent('app-updated'));
  }, 500);
}

import { initPWA } from "./pwa";

// Initialize PWA logic
initPWA();

// Hide the initial HTML loader
if (typeof window !== 'undefined' && (window as any).hideInitialLoader) {
  (window as any).hideInitialLoader();
}

// Top-level fault dome: any render-time exception that escapes every inner
// ErrorBoundary lands here instead of producing a white screen. Reports to
// PostHog when available so prod failures are observable rather than silent.
const handleRootError = (error: unknown, errorInfo: ErrorInfo) => {
  // eslint-disable-next-line no-console
  console.error('[root-error-boundary]', error, errorInfo);
  try {
    const ph = (window as unknown as { posthog?: { captureException?: (e: unknown, p?: Record<string, unknown>) => void } }).posthog;
    ph?.captureException?.(error, { componentStack: errorInfo.componentStack });
  } catch {
    // Telemetry must never re-throw out of the fallback path.
  }
};

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary
      onError={handleRootError}
      fallback={({ error, reset }) => <RootErrorScreen error={error} onReset={reset} />}
    >
      {isSupabaseConfigured ? <App /> : <MaintenanceScreen missingRequired={envValidation.missingRequired} />}
    </ErrorBoundary>
  </StrictMode>
);

// CRITICAL prod-monitoring: if the bundle shipped without required VITE_*
// env vars, the app degrades to the MaintenanceScreen. Surface this loudly
// so it shows up in monitoring (queued client error log + console.error
// with a recognisable tag the agent dashboards filter on).
if (!isSupabaseConfigured && typeof window !== 'undefined') {
  const tag = '[CRITICAL][env-missing] Production build is missing required VITE_* vars';
  // eslint-disable-next-line no-console
  console.error(tag, {
    missingRequired: envValidation.missingRequired,
    missingOptional: envValidation.missingOptional,
    href: window.location.href,
    build: typeof __APP_BUILD__ !== 'undefined' ? __APP_BUILD__ : 'unknown',
  });
  // Best-effort error log queueing — runs even when supabase client is null
  // because logError writes to localStorage first and syncs later.
  import('./hooks/useErrorLogging')
    .then(({ logError }) =>
      logError(tag, {
        kind: 'env_validation_failure',
        missingRequired: envValidation.missingRequired,
        missingOptional: envValidation.missingOptional,
        severity: 'critical',
      }),
    )
    .catch(() => {
      /* monitoring must never throw */
    });
}
